import { Loader2, RefreshCw, Search, X } from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";

import { summarizeSelection } from "../lib/selectionChips";
import { Button, ErrorState } from "../ui";

export type CategoryDto = {
	storeId: string;
	key: string;
	externalId: string | null;
	name: string;
	parentKey: string | null;
	productCount: number | null;
	selected: boolean;
	firstSeenAt: number;
	lastSeenAt: number;
	removedAt: number | null;
};

type TreeNode = CategoryDto & { children: TreeNode[] };

async function fetchCategories(storeId: string): Promise<CategoryDto[]> {
	const res = await fetch(`/api/stores/${encodeURIComponent(storeId)}/categories`);
	const data = (await res.json()) as { categories: CategoryDto[] };
	return data.categories;
}

async function patchSelection(storeId: string, selected: string[]): Promise<CategoryDto[]> {
	const res = await fetch(`/api/stores/${encodeURIComponent(storeId)}/categories`, {
		method: "PATCH",
		headers: { "content-type": "application/json" },
		body: JSON.stringify({ selected }),
	});
	const data = (await res.json()) as { categories: CategoryDto[] };
	return data.categories;
}

async function discoverNow(storeId: string): Promise<CategoryDto[]> {
	const res = await fetch(`/api/stores/${encodeURIComponent(storeId)}/categories/discover`, { method: "POST" });
	if (!res.ok) {
		const err = (await res.json().catch(() => ({}))) as { error?: string };
		throw new Error(err.error ?? `discover failed (${res.status})`);
	}
	const data = (await res.json()) as { categories: CategoryDto[] };
	return data.categories;
}

function buildTree(rows: CategoryDto[]): TreeNode[] {
	const byKey = new Map<string, TreeNode>();
	for (const r of rows) byKey.set(r.key, { ...r, children: [] });
	const roots: TreeNode[] = [];
	for (const node of byKey.values()) {
		if (node.parentKey && byKey.has(node.parentKey)) {
			byKey.get(node.parentKey)!.children.push(node);
		} else {
			roots.push(node);
		}
	}
	const sortFn = (a: TreeNode, b: TreeNode) => a.name.localeCompare(b.name, "nb");
	roots.sort(sortFn);
	for (const n of byKey.values()) n.children.sort(sortFn);
	return roots;
}

function collectKeys(node: TreeNode, out: Set<string>): void {
	out.add(node.key);
	for (const c of node.children) collectKeys(c, out);
}

/**
 * Filter tree by query: keep nodes whose name (or any descendant's name)
 * matches. Returns only the surviving subtree — ancestors stay visible so
 * the path is clear. Empty query returns the original tree.
 */
function filterTree(roots: TreeNode[], query: string): { roots: TreeNode[]; matchKeys: Set<string> } {
	const q = query.trim().toLowerCase();
	if (!q) return { roots, matchKeys: new Set() };
	const matchKeys = new Set<string>();

	function walk(node: TreeNode): TreeNode | null {
		const selfMatch = node.name.toLowerCase().includes(q);
		const filteredChildren = node.children.map((c) => walk(c)).filter((c): c is TreeNode => c != null);
		if (!selfMatch && filteredChildren.length === 0) return null;
		if (selfMatch) matchKeys.add(node.key);
		return { ...node, children: filteredChildren };
	}

	return {
		roots: roots.map(walk).filter((n): n is TreeNode => n != null),
		matchKeys,
	};
}

function fmtAge(ms: number): string {
	if (ms < 60_000) return "just now";
	if (ms < 60 * 60_000) return `${Math.round(ms / 60_000)}m ago`;
	if (ms < 24 * 60 * 60_000) return `${Math.round(ms / (60 * 60_000))}h ago`;
	return `${Math.round(ms / (24 * 60 * 60_000))}d ago`;
}

export type CategoriesPanelHandle = {
	isDirty: () => boolean;
	save: () => Promise<void>;
};

type Props = {
	storeId: string;
	onDirtyChange?: (dirty: boolean) => void;
};

export const CategoriesPanel = forwardRef<CategoriesPanelHandle, Props>(function CategoriesPanel(
	{ storeId, onDirtyChange },
	ref,
) {
	const [categories, setCategories] = useState<CategoryDto[]>([]);
	const [loading, setLoading] = useState(true);
	const [discovering, setDiscovering] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [originalSelected, setOriginalSelected] = useState<Set<string>>(new Set());
	const [query, setQuery] = useState("");

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		fetchCategories(storeId)
			.then((cats) => {
				if (cancelled) return;
				setCategories(cats);
				const sel = new Set(cats.filter((c) => c.selected).map((c) => c.key));
				setSelected(sel);
				setOriginalSelected(new Set(sel));
			})
			.catch((e) => !cancelled && setError((e as Error).message))
			.finally(() => !cancelled && setLoading(false));
		return () => {
			cancelled = true;
		};
	}, [storeId]);

	const tree = useMemo(() => buildTree(categories), [categories]);
	const { roots: filteredTree, matchKeys } = useMemo(() => filterTree(tree, query), [tree, query]);
	const dirty = useMemo(() => {
		if (selected.size !== originalSelected.size) return true;
		for (const k of selected) if (!originalSelected.has(k)) return true;
		return false;
	}, [selected, originalSelected]);

	useEffect(() => {
		onDirtyChange?.(dirty);
	}, [dirty, onDirtyChange]);

	useImperativeHandle(
		ref,
		() => ({
			isDirty: () => dirty,
			save: async () => {
				const cats = await patchSelection(storeId, [...selected]);
				setCategories(cats);
				const sel = new Set(cats.filter((c) => c.selected).map((c) => c.key));
				setSelected(sel);
				setOriginalSelected(new Set(sel));
			},
		}),
		[dirty, selected, storeId],
	);

	const toggleNode = (node: TreeNode) => {
		const subtree = new Set<string>();
		collectKeys(node, subtree);
		setSelected((prev) => {
			const next = new Set(prev);
			const allSelected = [...subtree].every((k) => next.has(k));
			for (const k of subtree) {
				if (allSelected) next.delete(k);
				else next.add(k);
			}
			return next;
		});
	};

	const deselectChip = (keys: readonly string[]) => {
		setSelected((prev) => {
			if (!keys.some((k) => prev.has(k))) return prev;
			const next = new Set(prev);
			for (const k of keys) next.delete(k);
			return next;
		});
	};

	const { chips, sharedPath } = useMemo(
		() =>
			summarizeSelection(
				selected,
				categories.map((c) => ({
					key: c.key,
					name: c.name,
					parentKey: c.parentKey,
					removed: c.removedAt != null,
				})),
			),
		[selected, categories],
	);

	const handleDiscover = async () => {
		setDiscovering(true);
		setError(null);
		try {
			const cats = await discoverNow(storeId);
			setCategories(cats);
			const valid = new Set(cats.map((c) => c.key));
			setSelected((prev) => {
				const next = new Set<string>();
				for (const k of prev) if (valid.has(k)) next.add(k);
				return next;
			});
		} catch (e) {
			setError((e as Error).message);
		} finally {
			setDiscovering(false);
		}
	};

	const totals = useMemo(() => {
		const all = categories.length;
		const removed = categories.filter((c) => c.removedAt != null).length;
		const sel = selected.size;
		const staleSelected = categories.filter((c) => c.removedAt != null && selected.has(c.key)).length;
		return { all, removed, sel, staleSelected };
	}, [categories, selected]);

	return (
		<div className="flex min-h-0 flex-1 flex-col gap-3">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<p className="text-xs text-muted">
					Pick the slices the sweep should collect. Empty selection ={" "}
					<span className="text-ink">collect everything</span>.
				</p>
				<Button
					variant="secondary"
					size="sm"
					onClick={handleDiscover}
					disabled={discovering}
					title="Re-fetch the store's category list now"
				>
					{discovering ? (
						<Loader2 className="size-3.5 animate-spin" aria-hidden />
					) : (
						<RefreshCw className="size-3.5" aria-hidden />
					)}
					Discover
				</Button>
			</div>

			{error && <ErrorState message={error} />}

			<div className="text-xs text-muted">
				{totals.all} categories · {totals.sel} selected
				{totals.removed > 0 && ` · ${totals.removed} stale`}
				{totals.staleSelected > 0 && (
					<>
						{" · "}
						<span className="text-ink">
							{totals.staleSelected} of your picks {totals.staleSelected === 1 ? "is" : "are"} stale
						</span>
					</>
				)}
			</div>

			{chips.length > 0 && (
				// Capped so the picks can never crowd out the tree they came from:
				// a single tick can select a whole subtree, and this block used to
				// grow until the list below it was one row tall.
				<div className="flex max-h-32 shrink-0 flex-col rounded-[10px] border border-hairline bg-paper px-3 py-2">
					<div className="mb-1.5 flex flex-wrap items-baseline gap-x-1.5 text-[11px] text-muted">
						<span className="font-medium">Selected ({selected.size})</span>
						{sharedPath.length > 0 && <span>under {sharedPath.map((n) => n.name).join(" → ")}</span>}
					</div>
					<ul className="flex min-h-0 flex-1 flex-wrap content-start gap-1.5 overflow-y-auto">
						{chips.map(({ key, path, subtreeKeys, hiddenCount, stale }) => {
							const label = path.map((n) => n.name).join(" → ");
							return (
								<li
									key={key}
									className="inline-flex h-fit items-center gap-1 rounded-full border border-hairline bg-raised py-0.5 pr-0.5 pl-2 text-xs text-ink"
								>
									<span>
										{path.map((n, i) => (
											<span key={n.key}>
												{i > 0 && <span className="mx-0.5 text-muted">→</span>}
												<span className={i === path.length - 1 ? "" : "text-muted"}>{n.name}</span>
											</span>
										))}
									</span>
									{hiddenCount > 0 && (
										<span
											className="rounded-full bg-ground px-1.5 text-[10px] font-medium text-muted"
											title={`Every category under ${label} is selected — ${hiddenCount} more`}
										>
											+{hiddenCount}
										</span>
									)}
									{stale && (
										<span
											className="rounded-full border border-hairline px-1.5 text-[10px] text-muted"
											title="The last discovery run did not see this category. The sweep still filters by it, but the store may have moved or removed it — run Discover to check."
										>
											Stale
										</span>
									)}
									<button
										type="button"
										onClick={() => deselectChip(subtreeKeys)}
										className="rounded-full p-0.5 text-muted hover:bg-ground hover:text-ink"
										aria-label={
											hiddenCount > 0
												? `Deselect ${label} and the ${hiddenCount} categories under it`
												: `Deselect ${label}`
										}
									>
										<X className="size-3" aria-hidden />
									</button>
								</li>
							);
						})}
					</ul>
				</div>
			)}

			<div className="relative">
				<Search
					className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted"
					aria-hidden
				/>
				<input
					type="search"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder="Search categories…"
					className="min-h-tap w-full rounded-md border border-hairline bg-ground pr-8 pl-8 text-[13px] text-ink outline-none placeholder:text-muted focus:border-line"
				/>
				{query && (
					<button
						type="button"
						onClick={() => setQuery("")}
						className="absolute top-1/2 right-1 -translate-y-1/2 rounded-md p-1 text-muted hover:bg-raised hover:text-ink"
						aria-label="Clear search"
					>
						<X className="size-3.5" aria-hidden />
					</button>
				)}
			</div>

			{loading && (
				<div className="rounded-[10px] border border-hairline bg-paper px-3 py-6 text-center text-[13px] text-muted">
					Loading…
				</div>
			)}

			{!loading && tree.length === 0 && (
				<div className="rounded-[10px] border border-hairline bg-paper px-3 py-6 text-center text-[13px] text-muted">
					No categories discovered yet. Click <span className="font-medium">Discover</span> to fetch them.
				</div>
			)}

			{!loading && tree.length > 0 && filteredTree.length === 0 && (
				<div className="rounded-[10px] border border-hairline bg-paper px-3 py-6 text-center text-[13px] text-muted">
					No categories match <span className="text-ink">&ldquo;{query}&rdquo;</span>.
				</div>
			)}

			{!loading && filteredTree.length > 0 && (
				<ul className="min-h-40 flex-1 overflow-auto rounded-[10px] border border-hairline bg-paper">
					{filteredTree.map((node) => (
						<CategoryNodeRow
							key={node.key}
							node={node}
							depth={0}
							selected={selected}
							matchKeys={matchKeys}
							onToggle={toggleNode}
						/>
					))}
				</ul>
			)}
		</div>
	);
});

function CategoryNodeRow({
	node,
	depth,
	selected,
	matchKeys,
	onToggle,
}: {
	node: TreeNode;
	depth: number;
	selected: Set<string>;
	matchKeys: Set<string>;
	onToggle: (n: TreeNode) => void;
}) {
	const subtree = useMemo(() => {
		const s = new Set<string>();
		collectKeys(node, s);
		return s;
	}, [node]);

	const subtreeSelected = useMemo(() => {
		let count = 0;
		for (const k of subtree) if (selected.has(k)) count++;
		return count;
	}, [subtree, selected]);

	const checkState: "checked" | "indeterminate" | "unchecked" =
		subtreeSelected === 0 ? "unchecked" : subtreeSelected === subtree.size ? "checked" : "indeterminate";

	const ageMs = Date.now() - node.lastSeenAt;
	const stale = node.removedAt != null;

	return (
		<>
			<li className="flex items-center gap-2 border-b border-hairline px-3 py-1.5 last:border-0">
				<span style={{ width: depth * 16 }} aria-hidden />
				<input
					type="checkbox"
					checked={checkState === "checked"}
					ref={(el) => {
						if (el) el.indeterminate = checkState === "indeterminate";
					}}
					onChange={() => onToggle(node)}
					className="size-4 cursor-pointer accent-ink"
					aria-label={`Toggle ${node.name}${node.children.length ? " and descendants" : ""}`}
				/>
				<span
					className={`min-w-0 flex-1 truncate text-[13px] text-ink ${matchKeys.has(node.key) ? "font-semibold" : ""}`}
				>
					{node.name}
					{node.children.length > 0 && (
						<span className="ml-1.5 text-[11px] text-muted">
							({subtreeSelected}/{subtree.size})
						</span>
					)}
				</span>
				{typeof node.productCount === "number" && (
					<span
						className="rounded-full bg-raised px-2 py-0.5 text-[11px] font-medium text-muted"
						title="Products in this category as reported by the store"
					>
						{node.productCount.toLocaleString()}
					</span>
				)}
				{stale ? (
					<span className="rounded-full border border-hairline px-1.5 py-0.5 text-[10px] text-muted">Stale</span>
				) : (
					<span className="text-[10px] text-muted" title="last seen">
						{fmtAge(ageMs)}
					</span>
				)}
			</li>
			{node.children.map((child) => (
				<CategoryNodeRow
					key={child.key}
					node={child}
					depth={depth + 1}
					selected={selected}
					matchKeys={matchKeys}
					onToggle={onToggle}
				/>
			))}
		</>
	);
}
